/**
 * InfinitSales - kanbanStore (flag kanban_store)
 *
 * Guarda em memoria (modulo, por aba) o ultimo board conhecido de cada funil,
 * para a tela /funis pintar na hora ao ser revisitada e revalidar por baixo
 * (stale-while-revalidate). Tambem lembra o ultimo funil aberto em
 * localStorage, para funis e board carregarem em paralelo na proxima visita.
 *
 * Formato de um board:
 *   { labels, buckets, hasMore, counts, valueTotals, maxLabels, labelsTotal,
 *     fetchedAt, deltaSince }
 *
 * Regras:
 *  - O store e escopado por usuario: trocar de conta na mesma aba limpa tudo
 *    (o board de um usuario nunca pinta para outro).
 *  - Funcoes puras (boardFromPayload, mergeDelta, applyLabelsChange,
 *    retotal) nunca mutam o board recebido: devolvem um objeto novo.
 *  - Nada aqui decide flag. Quem chama (TagsKanbanPage) so usa o store com
 *    kanban_store ligada; sem a flag o comportamento da tela e o de sempre.
 */

const LAST_FUNNEL_KEY = 'infinitsales:kanban:last_funnel';
export const UNLABELED_KEY = 'unlabeled';

let _scope = null;          // user_id dono dos boards em memoria
const _boards = new Map();  // funnelId -> board

/** Chave channel-agnostica do card: telefone no WhatsApp, chave opaca no IG/MS. */
export const cardKey = (c) => (c && (c.contact_ref || c.phone)) || '';

function ensureScope(userId) {
  const uid = userId || null;
  if (_scope !== uid) {
    _boards.clear();
    _scope = uid;
  }
}

function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

// ----------------------------------------------------------------------------
// Cache por funil
// ----------------------------------------------------------------------------
export function getBoard(userId, funnelId) {
  ensureScope(userId);
  return (funnelId && _boards.get(funnelId)) || null;
}

export function setBoard(userId, funnelId, board) {
  ensureScope(userId);
  if (!funnelId || !board) return;
  _boards.set(funnelId, board);
}

/** Atualiza campos de um board ja guardado (preserva fetchedAt/deltaSince). */
export function patchBoard(userId, funnelId, patch) {
  ensureScope(userId);
  const atual = funnelId && _boards.get(funnelId);
  if (!atual) return;
  _boards.set(funnelId, { ...atual, ...patch });
}

export function removeBoard(userId, funnelId) {
  ensureScope(userId);
  if (funnelId) _boards.delete(funnelId);
}

export function clear() {
  _boards.clear();
  _scope = null;
}

// ----------------------------------------------------------------------------
// Ultimo funil aberto (localStorage). O valor carrega o user_id para um funil
// de outra conta nunca ser reaberto na mesma maquina.
// ----------------------------------------------------------------------------
export function getLastFunnelId(userId) {
  try {
    const raw = localStorage.getItem(LAST_FUNNEL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed.u === userId && parsed.f ? String(parsed.f) : null;
  } catch {
    return null;
  }
}

export function setLastFunnelId(userId, funnelId) {
  try {
    if (!userId || !funnelId) return;
    localStorage.setItem(LAST_FUNNEL_KEY, JSON.stringify({ u: userId, f: funnelId }));
  } catch {
    // storage indisponivel (modo privado): segue sem lembrar
  }
}

// ----------------------------------------------------------------------------
// Conversao do payload do backend
// ----------------------------------------------------------------------------
/**
 * Board completo a partir do payload de GET /users/me/kanban-board (o mesmo
 * de hoje). `startedAt` (ISO UTC, instante em que o pedido saiu) vira o
 * proximo `delta_since` quando o servidor nao manda `until`.
 */
export function boardFromPayload(payload, startedAt) {
  const p = payload || {};
  return {
    labels: p.labels || [],
    buckets: p.buckets || { [UNLABELED_KEY]: [] },
    hasMore: p.has_more || {},
    counts: p.counts || {},
    valueTotals: p.value_totals || {},
    maxLabels: p.max_labels || null,
    labelsTotal: p.labels_total ?? (p.labels || []).length,
    fetchedAt: Date.now(),
    deltaSince: p.until || startedAt || new Date().toISOString(),
  };
}

/**
 * Funde um retorno `delta: true` (so as colunas tocadas) por cima do board
 * atual. `columns` lista as colunas tocadas; buckets/has_more/counts/
 * value_totals trazem so essas. Coluna tocada sem entrada em value_totals
 * soma zero (o servidor omite colunas sem valor).
 */
export function mergeDelta(base, payload, startedAt) {
  const p = payload || {};
  const b = base || boardFromPayload({}, startedAt);
  const tocadas = new Set([
    ...(Array.isArray(p.columns) ? p.columns : []),
    ...Object.keys(p.buckets || {}),
    ...Object.keys(p.counts || {}),
    ...Object.keys(p.has_more || {}),
  ]);
  const buckets = { ...(b.buckets || {}) };
  const hasMore = { ...(b.hasMore || {}) };
  const counts = { ...(b.counts || {}) };
  const valueTotals = { ...(b.valueTotals || {}) };
  tocadas.forEach((key) => {
    if (p.buckets && Object.prototype.hasOwnProperty.call(p.buckets, key)) buckets[key] = p.buckets[key] || [];
    if (p.has_more && Object.prototype.hasOwnProperty.call(p.has_more, key)) hasMore[key] = !!p.has_more[key];
    if (p.counts && Object.prototype.hasOwnProperty.call(p.counts, key)) counts[key] = p.counts[key];
    if (p.value_totals) valueTotals[key] = p.value_totals[key] || 0;
  });
  return {
    ...b,
    buckets,
    hasMore,
    counts,
    valueTotals,
    fetchedAt: Date.now(),
    deltaSince: p.until || startedAt || b.deltaSince,
  };
}

// ----------------------------------------------------------------------------
// Aplicacao de mudancas no cliente
// ----------------------------------------------------------------------------
function columnsOf(buckets, key) {
  const out = [];
  Object.entries(buckets || {}).forEach(([col, list]) => {
    if ((list || []).some((c) => cardKey(c) === key)) out.push(col);
  });
  return out;
}

/**
 * Recalcula counts e value_totals a partir da diferenca de pertencimento de
 * UM card entre dois estados de buckets (antes/depois). Usado pelo drag
 * optimistic e pelo evento SSE: coluna que perdeu o card conta -1 e desconta
 * o valor; coluna que ganhou conta +1 e soma o valor.
 */
export function retotal(prevBuckets, nextBuckets, contact, counts, valueTotals) {
  const key = cardKey(contact);
  const antes = new Set(columnsOf(prevBuckets, key));
  const depois = new Set(columnsOf(nextBuckets, key));
  const nextCounts = { ...(counts || {}) };
  const nextTotals = { ...(valueTotals || {}) };
  const valor = toNumber(contact && contact.value);
  antes.forEach((col) => {
    if (depois.has(col)) return;
    if (nextCounts[col] !== undefined) nextCounts[col] = Math.max(0, (nextCounts[col] || 0) - 1);
    if (valor && nextTotals[col] !== undefined) nextTotals[col] = Math.max(0, (nextTotals[col] || 0) - valor);
  });
  depois.forEach((col) => {
    if (antes.has(col)) return;
    nextCounts[col] = (nextCounts[col] || 0) + 1;
    if (valor) nextTotals[col] = (nextTotals[col] || 0) + valor;
  });
  return { counts: nextCounts, valueTotals: nextTotals };
}

/**
 * Aplica no cliente o evento CONTACT_LABELS_CHANGED
 * ({ phone, contact_ref, label_ids }): move o card entre as colunas do funil
 * ativo e ajusta counts/value_totals. `label_ids` e a lista COMPLETA de
 * etiquetas do contato (todos os funis).
 *
 * Devolve o board novo, ou null quando o card nao esta carregado em nenhuma
 * coluna (contato novo ou fora da pagina): ai quem chama revalida no servidor.
 *
 * "Sem etapa" no backend = nenhuma etiqueta VALIDA do catalogo (de qualquer
 * funil). Sem o catalogo inteiro (`catalogById`, so com multi_funnel), uma
 * etiqueta de outro funil e considerada valida: o card sai da tela e a
 * revalidacao corrige o raro caso de etiqueta apagada.
 */
export function applyLabelsChange(board, evt, catalogById) {
  if (!board || !evt || !Array.isArray(evt.label_ids)) return null;
  const ref = String(evt.contact_ref || '').trim();
  const phone = String(evt.phone || '').replace(/\D/g, '');
  const casa = (c) => {
    const k = cardKey(c);
    return (ref && k === ref) || (phone && (k === phone || String(c.phone || '') === phone));
  };

  let card = null;
  Object.values(board.buckets || {}).some((list) => {
    card = (list || []).find(casa) || null;
    return !!card;
  });
  if (!card) return null;

  const key = cardKey(card);
  const labelIds = evt.label_ids.map(String);
  const funnelIds = (board.labels || []).map((l) => l.id);
  const noFunil = funnelIds.filter((id) => labelIds.includes(id));
  let destino;
  if (noFunil.length) {
    destino = noFunil;
  } else {
    const temValidaFora = labelIds.some((id) => (
      catalogById ? !!catalogById[id] : true
    ));
    destino = temValidaFora ? [] : [UNLABELED_KEY];
  }

  const atualizado = { ...card, label_ids: labelIds };
  const buckets = {};
  Object.entries(board.buckets || {}).forEach(([col, list]) => {
    const semEle = (list || []).filter((c) => cardKey(c) !== key);
    buckets[col] = destino.includes(col) ? [atualizado, ...semEle] : semEle;
  });
  destino.forEach((col) => {
    if (!buckets[col]) buckets[col] = [atualizado];
  });

  const { counts, valueTotals } = retotal(board.buckets, buckets, atualizado, board.counts, board.valueTotals);
  return { ...board, buckets, counts, valueTotals };
}

const kanbanStore = {
  UNLABELED_KEY,
  cardKey,
  getBoard,
  setBoard,
  patchBoard,
  removeBoard,
  clear,
  getLastFunnelId,
  setLastFunnelId,
  boardFromPayload,
  mergeDelta,
  retotal,
  applyLabelsChange,
};

export default kanbanStore;
